import React from "react";
import FooterLogo from "../../assets/images/FooterLogo.png";
import {
  FaFacebook,
  FaWhatsapp,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { SITE, SOCIAL_LINKS } from "../../constants/site";
import { SiteNavLink } from "../layout/SiteNavLink";
import { NAV_LINKS } from "../../constants/navigation";
import { isRtlLanguage } from "../../utils/i18n";
import FooterPhoneLink from "./FooterPhoneLink";

const SOCIAL_ICONS = {
  facebook: FaFacebook,
  whatsapp: FaWhatsapp,
  phone: FaPhone,
};

const linkClass = "footer-router-link text-sm text-white/80 transition-colors";
const contactLinkClass =
  "caf-link-brand text-white/80 transition-colors break-words";

function ContactItem({ icon: Icon, children }) {
  return (
    <li className="flex items-start gap-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--caf-green)]" />
      <span className="min-w-0 text-[13px] leading-5 text-white/80">
        {children}
      </span>
    </li>
  );
}

function OfficeCard({ title, children }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-5">
      <h3 className="caf-footer-heading mb-4 text-xs font-bold uppercase tracking-widest">
        {title}
      </h3>
      <ul className="space-y-3">{children}</ul>
    </div>
  );
}

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isRtl = isRtlLanguage(i18n.language);

  return (
    <footer dir={isRtl ? "rtl" : "ltr"} className="bg-black text-white">
      <div className="mx-auto w-[92%] max-w-6xl py-10 sm:w-[85%] sm:py-12">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between sm:gap-12">
          <section className="max-w-lg">
            <img
              src={FooterLogo}
              alt={SITE.name}
              className="mb-4 h-auto w-[160px]"
              loading="lazy"
              decoding="async"
            />
            <p className="mb-5 text-start text-sm leading-relaxed text-white/70">
              {t(
                "We are CAF Sports Company, one of the leading companies in Egypt and the United Arab Emirates specialized in the field of sports construction and industrial flooring",
              )}
            </p>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ id, href, external }) => {
                const Icon = SOCIAL_ICONS[id];
                return (
                  <a
                    key={id}
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    aria-label={id}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-[var(--caf-green)] hover:text-[var(--caf-green)]"
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </a>
                );
              })}
            </div>
          </section>

          <section className="shrink-0 sm:min-w-[140px]">
            <h2 className="caf-footer-heading mb-3 text-base font-semibold">
              {t("Quick Links")}
            </h2>
            <ul className="flex flex-col gap-2">
              {NAV_LINKS.map(({ to, labelKey }) => (
                <li key={to}>
                  <SiteNavLink
                    to={to}
                    className={({ isActive }) =>
                      `${linkClass}${isActive ? " active" : ""}`
                    }
                  >
                    {t(labelKey)}
                  </SiteNavLink>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section className="mt-10 border-t border-white/10 pt-10">
          <h2 className="caf-footer-heading mb-6 text-base font-semibold">
            {t("Reach Us")}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            <OfficeCard title={t("General Inquiries")}>
              <ContactItem icon={FaEnvelope}>
                <a
                  href={`mailto:${SITE.emails.info}`}
                  className={contactLinkClass}
                >
                  {SITE.emails.info}
                </a>
              </ContactItem>
            </OfficeCard>

            <OfficeCard title={t("UAE Office")}>
              <ContactItem icon={FaEnvelope}>
                <a
                  href={`mailto:${SITE.emails.uae}`}
                  className={contactLinkClass}
                >
                  {SITE.emails.uae}
                </a>
              </ContactItem>
              <ContactItem icon={FaMapMarkerAlt}>
                <a
                  href={SITE.maps.uae}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={contactLinkClass}
                >
                  {t("United Arab Emirates")}
                </a>
              </ContactItem>
              <ContactItem icon={FaPhone}>
                <FooterPhoneLink phone={SITE.phones.uae} className={contactLinkClass} />
              </ContactItem>
            </OfficeCard>

            <OfficeCard title={t("Egypt Office")}>
              <ContactItem icon={FaEnvelope}>
                <a
                  href={`mailto:${SITE.emails.egypt}`}
                  className={contactLinkClass}
                >
                  {SITE.emails.egypt}
                </a>
              </ContactItem>
              <ContactItem icon={FaMapMarkerAlt}>
                <a
                  href={SITE.maps.egypt}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={contactLinkClass}
                >
                  {t("Nasr City, Egypt")}
                </a>
              </ContactItem>
              <ContactItem icon={FaPhone}>
                <div className="flex flex-col gap-1">
                  <FooterPhoneLink
                    phone={SITE.phones.egyptPrimary}
                    className={contactLinkClass}
                  />
                  <FooterPhoneLink
                    phone={SITE.phones.egyptSecondary}
                    className={contactLinkClass}
                  />
                </div>
              </ContactItem>
            </OfficeCard>
          </div>
        </section>

        <div className="mt-10 border-t border-white/10 pt-6 text-center">
          <a
            href={SITE.portfolioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-white/50 transition-colors hover:text-[var(--caf-green)] sm:text-sm"
          >
            {t(
              "Copyright © 2024 All rights reserved | This Website is made by Eslam Emam",
            )}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
